/**
 * GameplayBrandPanel — admin controls for the brand block (logo + name +
 * tagline) shown in the top-left corner of the GAME screens (VS Tempo /
 * VS Robot / VS Humano). Fully independent from the homepage header logo.
 */
import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { setPath } from "./setPath.js";
import { LANGS } from "./shared.jsx";

function TextStyleControls({ cfg, setCfg, base, title, accent, defaults, testPrefix }) {
  const s = cfg.gameplay_brand?.[base] || {};
  const set = (field, value) => setCfg(setPath(cfg, ["gameplay_brand", base, field], value));
  const v = (field) => s[field] ?? defaults[field];
  return (
    <div className={`mt-3 rounded-lg border p-3 space-y-2 ${accent}`}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-700">🎨 {title}</div>
      <div className="grid sm:grid-cols-2 gap-2">
        <div className="flex items-center gap-2">
          <Label className="text-[10px] w-14">Cor</Label>
          <Input type="color" value={v("color")} onChange={(e) => set("color", e.target.value)} className="h-8 w-14 p-1" data-testid={`${testPrefix}-color`} />
          <Input value={v("color")} onChange={(e) => set("color", e.target.value)} className="h-8 text-xs" />
        </div>
        <div>
          <Label className="text-[10px]">Tamanho ({v("size")}px)</Label>
          <Input type="range" min={6} max={40} value={v("size")} onChange={(e) => set("size", Number(e.target.value))} data-testid={`${testPrefix}-size`} />
        </div>
        <div>
          <Label className="text-[10px]">Peso ({v("weight")})</Label>
          <Input type="range" min={100} max={900} step={100} value={v("weight")} onChange={(e) => set("weight", Number(e.target.value))} data-testid={`${testPrefix}-weight`} />
        </div>
        <div>
          <Label className="text-[10px]">Espaçamento letras ({v("letter_spacing")}px)</Label>
          <Input type="range" min={-2} max={10} step={0.5} value={v("letter_spacing")} onChange={(e) => set("letter_spacing", Number(e.target.value))} data-testid={`${testPrefix}-tracking`} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 text-xs">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!v("uppercase")} onChange={(e) => set("uppercase", e.target.checked)} data-testid={`${testPrefix}-uppercase`} />
          <span>Maiúsculas</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={v("outline") !== false} onChange={(e) => set("outline", e.target.checked)} data-testid={`${testPrefix}-outline`} />
          <span>Contorno</span>
        </label>
        <div className="flex items-center gap-1.5">
          <Label className="text-[10px]">Cor do contorno</Label>
          <Input type="color" value={v("outline_color")} onChange={(e) => set("outline_color", e.target.value)} className="h-7 w-12 p-1" data-testid={`${testPrefix}-outline-color`} />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={v("shadow") !== false} onChange={(e) => set("shadow", e.target.checked)} data-testid={`${testPrefix}-shadow`} />
          <span>Sombra</span>
        </label>
      </div>
    </div>
  );
}

export function GameplayBrandPanel({ cfg, setCfg }) {
  const gb = cfg.gameplay_brand || {};
  return (
    <div className="space-y-3" data-testid="gameplay-brand-panel">
      <p className="text-[11px] text-slate-500">
        Bloco de marca no canto superior esquerdo dos ecrãs de jogo (VS Tempo · VS Robot · VS Humano).
        Formatação totalmente <b>independente</b> do logo do site. Textos vazios herdam o Nome/Tagline do site.
      </p>
      <div className="flex flex-wrap items-center gap-5">
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={gb.enabled !== false}
            onChange={(e) => setCfg(setPath(cfg, ["gameplay_brand", "enabled"], e.target.checked))}
            className="accent-emerald-600"
            data-testid="gp-brand-enabled"
          />
          <span className="font-bold text-slate-700">Mostrar logo/texto nos ecrãs de jogo</span>
        </label>
        <div className="min-w-[180px]">
          <Label className="text-[10px]">Tamanho do logo ({gb.logo_size ?? 42}px)</Label>
          <Input type="range" min={24} max={80} value={gb.logo_size ?? 42} onChange={(e) => setCfg(setPath(cfg, ["gameplay_brand", "logo_size"], Number(e.target.value)))} data-testid="gp-brand-logo-size" />
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs font-bold text-slate-700">Nome (linha grande) — vazio = usa o nome do site</Label>
        <div className="grid sm:grid-cols-3 gap-2">
          {LANGS.map(({ code, flag }) => (
            <Input
              key={code}
              value={gb.name?.[code] || ""}
              onChange={(e) => setCfg(setPath(cfg, ["gameplay_brand", "name", code], e.target.value))}
              placeholder={`${flag} ex: SUPERTMATIK™`}
              data-testid={`gp-brand-name-${code}`}
            />
          ))}
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs font-bold text-slate-700">Tagline (linha pequena) — vazio = usa a tagline do site</Label>
        <div className="grid sm:grid-cols-3 gap-2">
          {LANGS.map(({ code, flag }) => (
            <Input
              key={code}
              value={gb.tagline?.[code] || ""}
              onChange={(e) => setCfg(setPath(cfg, ["gameplay_brand", "tagline", code], e.target.value))}
              placeholder={`${flag} ex: BY @EUDACTICA`}
              data-testid={`gp-brand-tagline-${code}`}
            />
          ))}
        </div>
      </div>

      <TextStyleControls
        cfg={cfg} setCfg={setCfg}
        base="name_style"
        title="Formato · Nome"
        accent="border-indigo-200 bg-indigo-50/40"
        defaults={{ color: "#FFFFFF", size: 15, weight: 900, letter_spacing: -0.3, uppercase: false, outline: true, outline_color: "#0A0E2A", shadow: true }}
        testPrefix="gp-name"
      />
      <TextStyleControls
        cfg={cfg} setCfg={setCfg}
        base="tagline_style"
        title="Formato · Tagline"
        accent="border-amber-200 bg-amber-50/40"
        defaults={{ color: "#FFD93D", size: 9, weight: 700, letter_spacing: 2, uppercase: true, outline: true, outline_color: "#0A0E2A", shadow: true }}
        testPrefix="gp-tagline"
      />
    </div>
  );
}
